import React, { useEffect, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";

interface VideoThumbnailProps {
  videoUrl: string;
  width?: string;
  height?: string;
}

const VideoThumbnail: React.FC<VideoThumbnailProps> = ({ videoUrl, width = "100%", height = "auto" }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [attemptCount, setAttemptCount] = useState(0);

  useEffect(() => {
    setThumbnail(null);
    setAttemptCount(0);
  }, [videoUrl]);

  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) return;

    const context = canvas.getContext("2d");

    if (!context) return;

    // Load video metadata to get dimensions
    const loadVideoMetadata = () => {
      if (video.readyState >= 1) {
        // Set canvas dimensions to match video
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Try different time points to get a good thumbnail
        const timePoints = [
          Math.min(2, video.duration * 0.1), // 2 seconds or 10% of duration
          Math.min(1, video.duration * 0.05), // 1 second or 5% of duration
          Math.min(3, video.duration * 0.15), // 3 seconds or 15% of duration
          0 // Fallback to beginning
        ];
        
        const currentAttempt = attemptCount < timePoints.length ? attemptCount : timePoints.length - 1;
        const seekTime = timePoints[currentAttempt];
        video.currentTime = seekTime;
      }
    };

    // Capture the frame when the video is ready
    const captureFrame = () => {
      try {
        // Calculate crop area to focus on center-upper portion (where faces typically are)
        // Crop to 90% of width (full width) and 70% of height, positioned in upper-center
        const cropWidth = canvas.width * 0.9;
        const cropHeight = canvas.height * 0.7;
        const cropX = (canvas.width - cropWidth) / 2; // Center horizontally
        const cropY = canvas.height * 0.1; // Start from 10% from top (focus on upper portion)
        
        // Create a temporary canvas for the cropped image
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = cropWidth;
        tempCanvas.height = cropHeight;
        const tempContext = tempCanvas.getContext('2d');
        
        if (tempContext) {
          // Draw the cropped portion from the original canvas
          tempContext.drawImage(
            video,
            cropX, cropY, cropWidth, cropHeight, // Source rectangle
            0, 0, cropWidth, cropHeight // Destination rectangle
          );
          const thumbnailUrl = tempCanvas.toDataURL("image/jpeg", 0.8);
          
          // Check if the thumbnail is not just a black frame
          const imageData = tempContext.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
          const data = imageData.data;
          let hasContent = false;
          
          // Check if there's meaningful content (not just black/white)
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            // Check if pixel is not black (0,0,0) or very dark
            if (r > 20 || g > 20 || b > 20) {
              hasContent = true;
              break;
            }
          }
          
          if (hasContent || attemptCount >= 3) {
            setThumbnail(thumbnailUrl);
          } else {
            // Try next time point
            setAttemptCount(prev => prev + 1);
            loadVideoMetadata();
          }
        } else {
          // Fallback to original method if temp context fails
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          const thumbnailUrl = canvas.toDataURL("image/jpeg", 0.8);
          
          // Check if the thumbnail is not just a black frame
          const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;
          let hasContent = false;
          
          // Check if there's meaningful content (not just black/white)
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            // Check if pixel is not black (0,0,0) or very dark
            if (r > 20 || g > 20 || b > 20) {
              hasContent = true;
              break;
            }
          }
          
          if (hasContent || attemptCount >= 3) {
            setThumbnail(thumbnailUrl);
          } else {
            // Try next time point
            setAttemptCount(prev => prev + 1);
            loadVideoMetadata();
          }
        }
      } catch (error) {
        console.error("Error capturing video thumbnail:", error);
        // Try next attempt or fallback to 0
        if (attemptCount < 3) {
          setAttemptCount(prev => prev + 1);
          loadVideoMetadata();
        } else {
          video.currentTime = 0;
        }
      }
    };

    // Event listeners for loading data and capturing the frame
    video.addEventListener("loadedmetadata", loadVideoMetadata);
    video.addEventListener("seeked", captureFrame);

    // Cleanup event listeners on unmount
    return () => {
      video.removeEventListener("loadedmetadata", loadVideoMetadata);
      video.removeEventListener("seeked", captureFrame);
    };
  }, [videoUrl]);

  return (
    <div style={{ 
      width: '100%', 
      height: '100%', 
      position: 'relative', 
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      {thumbnail ? (
        <img 
          src={thumbnail} 
          alt="Video thumbnail" 
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center 30%', // Focus on upper-center where faces typically are
            borderRadius: 'inherit'
          }}
        />
      ) : (
        <div style={{
          width: '100%',
          height: '100%',
          backgroundColor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 'inherit',
          position: 'absolute',
          top: 0,
          left: 0
        }}>
          <CircularProgress 
            size={24} 
            sx={{ 
              color: '#756DE9',
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              }
            }} 
          />
        </div>
      )}
      {/* Hidden video and canvas elements */}
      <video
        ref={videoRef}
        src={videoUrl}
        crossOrigin="anonymous"
        preload="metadata"
        style={{ display: "none" }}
      />
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
};

export default VideoThumbnail;
